import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../lib/authContext';
import { setFormDirty } from '../lib/updateGuard';
import { ArrowLeft } from '@phosphor-icons/react';

export default function OrgLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const showResetBanner = params.get('reset') === '1';

  useEffect(() => { setFormDirty('login', true); return () => setFormDirty('login', false); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success('Logged in');
      navigate('/account');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Login failed';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5] flex flex-col items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md mb-4">
        <Link to="/home" className="inline-flex items-center gap-1.5 text-sm text-[#455561] font-semibold hover:text-[#1B4332] transition-colors" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" weight="bold" /> Back to home
        </Link>
      </div>
      <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl space-y-6">
        {showResetBanner && (
          <div className="bg-[#E8F5E9] text-[#2E7D32] text-sm font-semibold px-4 py-3 rounded-lg text-center" data-testid="reset-success-banner">
            Password updated. Please log in.
          </div>
        )}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1D242B]">Log in</h1>
          <p className="text-sm text-[#455561] mt-1">Access your Fore Tour organiser account</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="login-email" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" required data-testid="login-password" />
            <Link to="/organiser/forgot-password" className="text-xs text-[#455561] hover:text-[#1B4332] mt-1.5 inline-block" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-semibold" data-testid="login-submit">
            {submitting ? 'Logging in...' : 'Log in'}
          </Button>
        </form>
        <p className="text-center text-sm text-[#455561]">Don't have an account? <Link to="/signup" className="text-[#1B4332] font-semibold hover:underline">Sign up</Link></p>
      </Card>
    </div>
  );
}
